<template>
  <div class="col-sm-12" :class="grid ? 'col-md-' + grid : 'col-md-12'">
    <div class="form-group mb-2">
      <label class="d-block font-weight-bold">
        {{ label }}
        <span v-if="required" class="text-danger">*</span>
      </label>

      <div class="editor-comentario">
        <VueEditor
          v-model="text"
          :disabled="disabled"
          :placeholder="placeholder"
          :editorToolbar="editorToolbar"
          :useCustomImageHandler="true"
          @image-added="handleImageAdded"
          @imageAdded="handleImageAdded"
          @input="changeText"
          @focus="focus"
          @blur="blur"
        />

        <textarea
          v-model="text"
          :required="required"
          style="opacity: 0; height: 1px; position: absolute"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { VueEditor } from "vue2-editor";

export default {
  components: { VueEditor },

  props: {
    value: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: "",
    },
    required: {
      type: Boolean,
      default: false,
    },
    grid: {
      default: null,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: "",
    },
    mt: {
      default: null,
    },

    /**
     * Valores pensados para campo de comentário.
     * Mantém imagem legível e reduz bem o peso do base64.
     */
    imageMaxWidth: {
      type: Number,
      default: 600,
    },
    imageMaxHeight: {
      type: Number,
      default: 600,
    },
    imageQuality: {
      type: Number,
      default: 0.55,
    },
    imageOutputType: {
      type: String,
      default: "image/jpeg",
    },
  },

  data() {
    return {
      text: this.value || "",

      editorToolbar: [
        [{ header: [false, 1, 2, 3] }],

        ["bold", "italic", "underline", "strike"],

        [{ color: [] }, { background: [] }],

        [
          { align: "" },
          { align: "center" },
          { align: "right" },
          { align: "justify" },
        ],

        ["blockquote", "code-block"],

        [{ list: "ordered" }, { list: "bullet" }],

        [{ indent: "-1" }, { indent: "+1" }],

        ["link", "image"],

        ["clean"],
      ],
    };
  },

  watch: {
    value: {
      immediate: true,
      handler(value) {
        const newValue = value || "";

        if (newValue !== this.text) {
          this.text = newValue;
        }
      },
    },
  },

  methods: {
    focus() {
      this.$emit("onfocus", true);
    },

    blur() {
      this.$emit("onblur", false);
    },

    changeText(value) {
      this.text = value;
      this.$emit("input", this.text);
    },

    async handleImageAdded(file, Editor, cursorLocation, resetUploader) {
      if (!file || !file.type || !file.type.startsWith("image/")) {
        this.$emit("image-error", "Arquivo inválido.");
        return;
      }

      try {
        const resizedImage = await this.resizeImage(file);

        const position =
          typeof cursorLocation === "number"
            ? cursorLocation
            : Editor.getSelection()
            ? Editor.getSelection().index
            : Editor.getLength();

        Editor.insertEmbed(position, "image", resizedImage, "user");
        Editor.setSelection(position + 1);

        this.text = Editor.root.innerHTML;
        this.$emit("input", this.text);
      } catch (error) {
        console.error("Erro ao redimensionar imagem:", error);
        this.$emit("image-error", error);
      } finally {
        if (typeof resetUploader === "function") {
          resetUploader();
        }
      }
    },

    resizeImage(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (event) => {
          const image = new Image();

          image.onload = () => {
            const originalWidth = image.width;
            const originalHeight = image.height;

            const resizeRatio = Math.min(
              this.imageMaxWidth / originalWidth,
              this.imageMaxHeight / originalHeight,
              1
            );

            const newWidth = Math.round(originalWidth * resizeRatio);
            const newHeight = Math.round(originalHeight * resizeRatio);

            const canvas = document.createElement("canvas");
            canvas.width = newWidth;
            canvas.height = newHeight;

            const context = canvas.getContext("2d");

            /**
             * Como o padrão está em JPEG, preenche o fundo.
             * Isso evita imagem PNG transparente ficar com fundo estranho.
             */
            if (this.imageOutputType === "image/jpeg") {
              context.fillStyle = "#ffffff";
              context.fillRect(0, 0, newWidth, newHeight);
            }

            context.drawImage(image, 0, 0, newWidth, newHeight);

            const resizedImage = canvas.toDataURL(
              this.imageOutputType,
              this.imageQuality
            );

            resolve(resizedImage);
          };

          image.onerror = reject;
          image.src = event.target.result;
        };

        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
  },
};
</script>

<style lang="css">
@import "~vue2-editor/dist/vue2-editor.css";
@import "~quill/dist/quill.core.css";
@import "~quill/dist/quill.bubble.css";
@import "~quill/dist/quill.snow.css";

.editor-comentario .ql-container {
  min-height: 180px;
  font-size: 14px;
}

.editor-comentario .ql-editor {
  min-height: 180px;
  max-height: 600px;
  overflow-y: auto;
}

.editor-comentario .ql-editor img {
  max-width: 720px;
  width: auto;
  height: auto;
  display: block;
  margin: 10px 0;
  border-radius: 4px;
}

@media (max-width: 992px) {
  .editor-comentario .ql-editor img {
    max-width: 100%;
  }
}
</style>
